// ─── src/components/elevateFeedback/ElevateFeedbackCharts.tsx ────────────────
//
// KPIs, highlights and charts for the Elevate Damascus 2026 participant
// feedback. Same card look as the GEW feedback charts; the 1–5 rating
// distribution chart is reused from there.

import type { FC, ReactNode } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Cell, Tooltip, ResponsiveContainer, LabelList,
} from "recharts";
import {
  GraduationCap, Lightbulb, MessageSquare, MessagesSquare, Mic, Network, Star, TrendingUp, Building2,
} from "lucide-react";
import { StatCard } from "../ui/StatCard";
import { RatingChart } from "../gewFeedback/FeedbackCharts";
import {
  DAY_LABEL, DURATION_LABEL, ORG_LABEL,
  PILLAR_LABEL, SESSION_LABEL, highlights, pct, sessionDay, type ElevateSummary, type NpsStats,
} from "./analysis";

const AXIS = { fill: "#9ca3af", fontSize: 11 };
const NPS_COLOR = (v: number) => (v <= 6 ? "#ef4444" : v <= 8 ? "#f59e0b" : "#00DC92");
const DAY_COLOR: Record<string, string> = { day1: "#00DC92", day2: "#6366f1", day3: "#f59e0b" };
const scoreColor = (v: number | null) =>
  v == null ? "#6b7280" : v >= 4 ? "#00DC92" : v >= 3 ? "#f59e0b" : "#ef4444";
const fmt = (v: number | null) => (v == null ? "—" : `${v} / 5`);

const Tip: FC<{ active?: boolean; payload?: { payload: { tip: string; sub?: string } }[] }> = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-brand-card border border-brand-border rounded-xl px-4 py-3 shadow-xl text-sm max-w-xs">
      <p className="text-white font-semibold" dir="auto">{p.tip}</p>
      {p.sub && <p className="text-brand-muted mt-0.5">{p.sub}</p>}
    </div>
  );
};

const Card: FC<{ title: string; subtitle?: string; right?: ReactNode; children: ReactNode }> = ({
  title, subtitle, right, children,
}) => (
  <div className="card p-5">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-white font-semibold text-sm">{title}</p>
        {subtitle && <p className="text-brand-muted text-xs mt-0.5">{subtitle}</p>}
      </div>
      {right}
    </div>
    {children}
  </div>
);

// ─── KPIs ────────────────────────────────────────────────────────────────────

export const ElevateFeedbackKpis: FC<{ s: ElevateSummary }> = ({ s }) => {
  const npsTotal = s.nps.promoters + s.nps.passives + s.nps.detractors;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={MessageSquare} label="Responses" value={s.responses} accent="bg-brand-green" />
        <StatCard icon={Star} label="Overall rating" value={fmt(s.overall.average)} accent="bg-brand-teal" />
        <div className="card p-5 flex items-center gap-4">
          <div className="bg-brand-green w-12 h-12 rounded-xl flex items-center justify-center shrink-0">
            <TrendingUp size={22} className="text-white" />
          </div>
          <div className="min-w-0">
            <p className="text-brand-muted text-sm truncate">NPS (−100 to +100)</p>
            <p className="text-white text-2xl font-bold">
              {s.nps.score == null ? "—" : `${s.nps.score > 0 ? "+" : ""}${s.nps.score}`}
            </p>
            {npsTotal > 0 && (
              <p className="text-[11px] text-brand-muted mt-0.5">
                <span className="text-emerald-400">{pct(s.nps.promoters, npsTotal)}% promoters</span>
                {" · "}<span className="text-amber-400">{pct(s.nps.passives, npsTotal)}% passive</span>
                {" · "}<span className="text-red-400">{pct(s.nps.detractors, npsTotal)}% detractors</span>
              </p>
            )}
          </div>
        </div>
        <StatCard icon={GraduationCap} label="Mentoring confidence" value={fmt(s.mentorConfidence.average)} accent="bg-brand-teal" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Mic} label="Speakers" value={fmt(s.speakers.average)} accent="bg-brand-green" />
        <StatCard icon={MessagesSquare} label="Interaction & workshops" value={fmt(s.interaction.average)} accent="bg-brand-teal" />
        <StatCard icon={Network} label="Network built" value={fmt(s.networking.average)} accent="bg-brand-green" />
        <StatCard icon={Building2} label="Organisation (all aspects)" value={fmt(s.organizationAvg)} accent="bg-brand-teal" />
      </div>
    </div>
  );
};

// ─── Highlights ──────────────────────────────────────────────────────────────

export const HighlightsCard: FC<{ s: ElevateSummary }> = ({ s }) => {
  const items = highlights(s);
  if (!items.length) return null;
  const dot = { good: "bg-emerald-400", warn: "bg-amber-400", neutral: "bg-brand-muted" };
  return (
    <div className="card p-5">
      <p className="text-white font-semibold text-sm flex items-center gap-2">
        <Lightbulb size={15} className="text-brand-green" /> Highlights
      </p>
      <p className="text-brand-muted text-xs mt-0.5">Worked out automatically from the {s.responses} responses.</p>
      <ul className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-2.5">
        {items.map((h, i) => (
          <li key={i} className="flex items-start gap-2.5 text-sm">
            <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${dot[h.tone]}`} />
            <span className="text-brand-muted min-w-0">
              <span className="text-white">{h.text}</span>
              {h.ar && (
                <>
                  {": "}
                  <bdi dir="rtl" className="text-brand-green">{h.ar}</bdi>
                </>
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

// ─── Charts ──────────────────────────────────────────────────────────────────

const DayChart: FC<{ s: ElevateSummary }> = ({ s }) => {
  const data = s.days.map((d) => ({
    key: d.key,
    label: DAY_LABEL[d.key],
    value: d.average ?? 0,
    shown: d.average == null ? "—" : `${d.average}`,
    tip: DAY_LABEL[d.key],
    sub: `Average ${d.average ?? "—"} / 5 · attended by ${d.attended} of ${s.responses}`,
  }));
  return (
    <Card title="Rating per day" subtitle="Average 1–5, from those who attended that day">
      <div className="h-52 mt-3">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 18, right: 4, bottom: 0, left: -24 }}>
            <XAxis dataKey="label" tick={AXIS} axisLine={false} tickLine={false} />
            <YAxis domain={[0, 5]} ticks={[0, 1, 2, 3, 4, 5]} tick={AXIS} axisLine={false} tickLine={false} />
            <Tooltip content={<Tip />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
            <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={56}>
              {data.map((d) => <Cell key={d.key} fill={DAY_COLOR[d.key]} />)}
              <LabelList dataKey="shown" position="top" fill="#e5e7eb" fontSize={12} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <ul className="mt-3 space-y-1 text-xs">
        {s.days.map((d) => (
          <li key={d.key} className="flex items-center justify-between gap-3">
            <span className="text-brand-muted truncate" dir="rtl" title={DAY_LABEL[d.key]}>
              <span className="inline-block w-2.5 h-2.5 rounded-full ml-1.5" style={{ background: DAY_COLOR[d.key] }} />
              {DAY_LABEL[d.key]}
            </span>
            <span className="text-white shrink-0">
              {d.attended} attended
              <span className="text-brand-muted"> · {pct(d.attended, s.responses)}%</span>
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
};

/** Ranked horizontal bars with Arabic labels kept readable (HTML, not SVG). */
const BarList: FC<{ items: { key: string; label: string; count: number; color: string }[]; total: number }> = ({
  items, total,
}) => {
  const max = Math.max(1, ...items.map((x) => x.count));
  return (
    <ul className="mt-4 space-y-2">
      {items.map((x, i) => (
        <li key={x.key} className="grid grid-cols-[1.5rem_minmax(0,1fr)] sm:grid-cols-[1.5rem_minmax(0,22rem)_minmax(0,1fr)] items-center gap-x-3 gap-y-1">
          <span className="text-brand-muted text-xs text-right">{i + 1}</span>
          <span className="text-white text-sm truncate" dir="rtl" title={x.label}>{x.label}</span>
          <div className="col-start-2 sm:col-start-3 flex items-center gap-2">
            <div className="flex-1 h-3 rounded-full bg-brand-border/40 overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${(x.count / max) * 100}%`, background: x.color }} />
            </div>
            <span className={`text-xs w-16 text-right shrink-0 ${x.count ? "text-white" : "text-brand-muted"}`}>
              {x.count} · {pct(x.count, total)}%
            </span>
          </div>
        </li>
      ))}
    </ul>
  );
};

const SessionsChart: FC<{ s: ElevateSummary }> = ({ s }) => {
  return (
    <Card
      title="Most useful sessions"
      subtitle={`How often each session was picked (up to 3 per person) · all ${s.sessions.length} sessions`}
      right={
        <div className="flex gap-3 text-[11px] text-brand-muted shrink-0">
          {(["day1", "day2", "day3"] as const).map((d) => (
            <span key={d} className="inline-flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: DAY_COLOR[d] }} />
              <bdi dir="rtl">{DAY_LABEL[d]}</bdi>
            </span>
          ))}
        </div>
      }
    >
      <BarList
        total={s.responses}
        items={s.sessions.map((x) => {
          const day = sessionDay(x.key);
          return { key: x.key, label: SESSION_LABEL[x.key] ?? x.key, count: x.count, color: day ? DAY_COLOR[day] : "#6b7280" };
        })}
      />
    </Card>
  );
};

/** One bar per item: its average 1–5 rating. */
const AverageBars: FC<{ title: string; subtitle: string; items: { key: string; average: number | null }[]; labels: Record<string, string> }> = ({
  title, subtitle, items, labels,
}) => (
  <Card title={title} subtitle={subtitle}>
    <ul className="mt-4 space-y-3">
      {items.map((p) => (
        <li key={p.key}>
          <div className="flex items-center justify-between gap-3">
            <span className="text-white text-sm truncate" dir="rtl" title={labels[p.key]}>{labels[p.key]}</span>
            <span className="text-sm font-semibold shrink-0" style={{ color: scoreColor(p.average) }}>{fmt(p.average)}</span>
          </div>
          <div className="h-2.5 mt-1.5 rounded-full bg-brand-border/40 overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${((p.average ?? 0) / 5) * 100}%`, background: scoreColor(p.average) }} />
          </div>
        </li>
      ))}
    </ul>
  </Card>
);

const NpsChart: FC<{ nps: NpsStats }> = ({ nps }) => {
  const total = nps.promoters + nps.passives + nps.detractors;
  const data = nps.distribution.map((value, v) => ({
    label: String(v),
    value,
    tip: `Score ${v}`,
    sub: `${value} response${value !== 1 ? "s" : ""} (${pct(value, total)}%)`,
  }));
  return (
    <Card
      title="Net Promoter Score"
      subtitle="How likely they are to recommend Elevate (0–10)"
      right={<p className="text-brand-green text-xl font-bold shrink-0">{nps.score == null ? "—" : nps.score}</p>}
    >
      <div className="h-52 mt-3">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 18, right: 4, bottom: 0, left: -24 }}>
            <XAxis dataKey="label" tick={AXIS} axisLine={false} tickLine={false} />
            <YAxis allowDecimals={false} tick={AXIS} axisLine={false} tickLine={false} />
            <Tooltip content={<Tip />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
            <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={36}>
              {data.map((_, v) => <Cell key={v} fill={NPS_COLOR(v)} />)}
              <LabelList dataKey="value" position="top" fill="#9ca3af" fontSize={11} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs mt-3 text-brand-muted">
        <span><span className="inline-block w-2.5 h-2.5 rounded-full mr-1.5" style={{ background: "#00DC92" }} />Promoters (9–10): {nps.promoters} · {pct(nps.promoters, total)}%</span>
        <span><span className="inline-block w-2.5 h-2.5 rounded-full mr-1.5" style={{ background: "#f59e0b" }} />Passives (7–8): {nps.passives} · {pct(nps.passives, total)}%</span>
        <span><span className="inline-block w-2.5 h-2.5 rounded-full mr-1.5" style={{ background: "#ef4444" }} />Detractors (0–6): {nps.detractors} · {pct(nps.detractors, total)}%</span>
        <span>NPS = % promoters − % detractors{nps.average != null ? ` · average ${nps.average}` : ""}</span>
      </div>
    </Card>
  );
};

/** One single-choice question as a split bar plus a labelled legend. */
const SplitChart: FC<{
  title: string;
  subtitle?: string;
  counts: Record<string, number>;
  labels: Record<string, string>;
  colors: Record<string, string>;
  total: number;
}> = ({ title, subtitle, counts, labels, colors, total }) => (
  <Card title={title} subtitle={subtitle ?? `${total} answer${total !== 1 ? "s" : ""}`}>
    <div className="flex h-4 mt-4 rounded-full overflow-hidden bg-brand-border/40">
      {Object.entries(counts).map(([k, v]) =>
        v ? <div key={k} style={{ width: `${pct(v, total)}%`, background: colors[k] ?? "#6b7280" }} title={`${labels[k] ?? k}: ${v}`} /> : null,
      )}
    </div>
    <ul className="mt-4 space-y-2">
      {Object.entries(counts).map(([k, v]) => (
        <li key={k} className="flex items-center justify-between gap-3 text-sm">
          <span className="inline-flex items-center gap-2 min-w-0">
            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: colors[k] ?? "#6b7280" }} />
            <bdi dir="rtl" className="text-white truncate">{labels[k] ?? k}</bdi>
          </span>
          <span className="text-white shrink-0">
            {v}<span className="text-brand-muted"> · {pct(v, total)}%</span>
          </span>
        </li>
      ))}
    </ul>
  </Card>
);

export const ElevateFeedbackCharts: FC<{ s: ElevateSummary }> = ({ s }) => {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <DayChart s={s} />
        <AverageBars
          title="Programme pillars"
          subtitle="Average 1–5: how well Elevate delivered on each pillar"
          items={s.pillars}
          labels={PILLAR_LABEL}
        />
      </div>
      <SessionsChart s={s} />
      <NpsChart nps={s.nps} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <AverageBars
          title="Organisation"
          subtitle="Average 1–5 for each aspect"
          items={s.organization}
          labels={ORG_LABEL}
        />
        <SplitChart
          title="Programme length"
          counts={s.duration}
          labels={DURATION_LABEL}
          colors={{ short: "#f59e0b", right: "#00DC92", long: "#ef4444" }}
          total={s.responses}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <RatingChart title="Overall experience" question="" stats={s.overall} />
        <RatingChart title="Speakers" question="" stats={s.speakers} />
        <RatingChart title="Interaction & workshops" question="" stats={s.interaction} />
        <RatingChart title="Network built" question="" stats={s.networking} />
        <RatingChart title="Confidence mentoring a founder" question="" stats={s.mentorConfidence} />
      </div>
    </div>
  );
};
